"use client";

import { useState, useMemo, FC, useEffect, useCallback } from "react";
import { DateRange } from "react-day-picker";
import { format, endOfMonth, startOfDay, endOfDay } from "date-fns";
import { es } from "date-fns/locale";
import { BarChart, CartesianGrid, XAxis, YAxis, ResponsiveContainer, Bar } from "recharts";
import { Calendar as CalendarIcon, DollarSign, Package, Percent, TrendingUp, Settings, Loader2, ShoppingCart, BarChart3, Save, FileJson, BarChartHorizontal, RefreshCw, Trash2 } from "lucide-react";

import { useSalesReport } from "@/hooks/useSalesReport";
import { FunctionsService } from "@/services/functions.service";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
// Table components replaced with native HTML tables for scroll control
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/useToast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Header } from "../layout/Header";

type Channel = 'web' | 'pos' | 'all';

interface IndicatorsConfig {
  channel: Channel;
  showBrands: boolean;
  showLines: boolean;
}

interface ChannelData {
    units: number;
    netSales: number;
    avgPrice: number;
    orders: number;
    upt: number;
    avgTicket: number;
    brands: Record<string, { units: number; netSales: number }>;
    lines: Record<string, { units: number; netSales: number }>;
}

const KNOWN_BRANDS = [
    "Mixed Emotion", "FRD", "Escobar", "Pure", "Monastery", "Layaway",
    "Pre owned", "Supreme", "Godspeed", "Meclier", "Gold Star", "Reference",
    "Hellstar", "New Era", "Air Jordan", "Mystery", "26 Red", "Saint Theory",
    "Nike", "Sinners", "Adidas", "Homme Femme", "Vintage demand", "Jordan",
    "OffCorss", "Gabetes"
];

const mapProductLine = (line: string): string => {
    if (!line) return "Otros";
    const lowerLine = line.toLowerCase();
    
    let result = line;

    if (['gorras', 'gorra', 'gorras snapback'].includes(lowerLine)) {
        result = 'Gorras';
    } else if (['cargo denim pants', 'mahon', 'cargo'].includes(lowerLine)) {
        result = 'Pantalones';
    } else if (['tenis', 'calzado'].includes(lowerLine)) {
        result = 'Calzado';
    } else if (['camisas', 'camisa', 'tshirt'].includes(lowerLine)) {
        result = 'Camisas';
    }
    
    return result.charAt(0).toUpperCase() + result.slice(1);
};

export function IndicatorsDashboard() {
  const { toast } = useToast();

  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: startOfDay(new Date()),
    to: endOfDay(new Date()),
  });

  const { report, loading, getSalesReport } = useSalesReport();
  const [config, setConfig] = useState<IndicatorsConfig>({
    channel: 'all',
    showBrands: true,
    showLines: true,
  });
  const [forceShopify, setForceShopify] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<'today' | 'month-to-date' | 'full-month' | 'custom'>('today');

  const handleGenerateReport = useCallback((currentDate: DateRange | undefined) => {
    if (!currentDate?.from || !currentDate?.to) return;
    getSalesReport(startOfDay(currentDate.from), endOfDay(currentDate.to), forceShopify);
  }, [getSalesReport, forceShopify]);

  useEffect(() => {
    if (dateRange?.from && dateRange.to) {
        handleGenerateReport(dateRange);
    }
  }, [dateRange, handleGenerateReport]);

  const channelData: ChannelData | null = useMemo(() => {
    if (!report) return null;

    const filteredOrders = report.orders.filter(order => {
        if (config.channel === 'all') return true;
        const orderSourceName = (order as any).source_name || (order as any).sourceName;
        return orderSourceName === config.channel;
    });

    if(filteredOrders.length === 0) return { units: 0, netSales: 0, avgPrice: 0, orders: 0, upt: 0, avgTicket: 0, brands: {}, lines: {} };

    const data: ChannelData = {
        units: 0, netSales: 0, orders: filteredOrders.length, upt: 0, avgTicket: 0, avgPrice: 0, brands: {}, lines: {}
    };

    for (const order of filteredOrders) {
        data.netSales += (order as any).netSales || 0;
        if (Array.isArray((order as any).products)) {
            for(const item of (order as any).products) {
                const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
                data.units += quantity;
                const itemPrice = typeof item.price === 'number'
                  ? item.price
                  : Number.parseFloat(typeof item.price === 'string' ? item.price : '0');
                const safePrice = Number.isFinite(itemPrice) ? itemPrice : 0;

                if (config.showBrands) {
                    const combinedSource = [
                        item.name.toLowerCase(),
                        typeof item.tags === 'string' ? item.tags.toLowerCase() : ''
                    ].join(' ');

                    for (const brand of KNOWN_BRANDS) {
                        if (combinedSource.includes(brand.toLowerCase())) {
                            const normalizedBrand = brand === 'Adidas' ? 'Adidas' : brand;
                            if (!data.brands[normalizedBrand]) data.brands[normalizedBrand] = { units: 0, netSales: 0 };
                            data.brands[normalizedBrand].units += quantity;
                            data.brands[normalizedBrand].netSales += quantity * safePrice;
                        }
                    }
                }

                if (config.showLines) {
                  const rawLineSource =
                    typeof item.product_type === 'string' && item.product_type.trim().length > 0
                      ? item.product_type
                      : typeof item.tags === 'string' && item.tags.trim().length > 0
                        ? item.tags.split(',')[0]
                        : 'Otros';

                  const lineName = mapProductLine(rawLineSource.trim());
                  if (!data.lines[lineName]) data.lines[lineName] = { units: 0, netSales: 0 };
                  data.lines[lineName].units += quantity;
                  data.lines[lineName].netSales += quantity * safePrice;
                }
            }
        }
    }
    
    data.avgTicket = data.netSales > 0 && data.orders > 0 ? data.netSales / data.orders : 0;
    data.upt = data.units > 0 && data.orders > 0 ? data.units / data.orders : 0;
    data.avgPrice = data.netSales > 0 && data.units > 0 ? data.netSales / data.units : 0;

    return data;
  }, [report, config]);

  const handleDateChange = (newDate: DateRange | undefined) => {
    let finalDate = newDate;
    if (newDate?.from && !newDate.to) {
      finalDate = { ...newDate, to: endOfDay(newDate.from) };
    }
    setDateRange(finalDate);
    setSelectedPreset('custom');
  };

  const handlePresetChange = useCallback(
    (preset: 'today' | 'month-to-date' | 'full-month') => {
      const now = new Date()
      let nextRange: DateRange

      if (preset === 'today') {
        nextRange = { from: startOfDay(now), to: endOfDay(now) }
      } else if (preset === 'month-to-date') {
        const monthStart = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1))
        nextRange = { from: monthStart, to: endOfDay(now) }
      } else {
        const monthStart = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1))
        nextRange = { from: monthStart, to: endOfDay(endOfMonth(now)) }
      }

      setSelectedPreset(preset)
      setDateRange(nextRange)
      handleGenerateReport(nextRange)
    },
    [handleGenerateReport]
  )

  const handleGenerateClick = useCallback(() => {
    handleGenerateReport(dateRange)
  }, [dateRange, handleGenerateReport])

  const handleClearCache = useCallback(async () => {
    try {
      const response = await FunctionsService.clearCache(
        dateRange?.from && dateRange?.to ? { from: dateRange.from, to: dateRange.to } : undefined
      )

      if (response.error) {
        toast({
          title: 'Error al limpiar caché',
          description: response.error,
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Caché limpiado',
          description: response.data?.message ?? 'La próxima consulta utilizará información actualizada.',
        })
      }
    } catch (error: any) {
      toast({
        title: 'Error al limpiar caché',
        description: error?.message ?? 'No se pudo limpiar la caché.',
        variant: 'destructive',
      })
    }
  }, [dateRange, toast])

  const formatCurrency = (amount: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

  const currentFrom = dateRange?.from ?? new Date();
  const currentTo = dateRange?.to ?? new Date();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
    <div className="mx-auto w-full max-w-[1600px] px-4 py-8 sm:px-6 lg:px-10">
        <Header />

        <Card className="mb-8 bg-white/5 border-white/10 backdrop-blur-xl">
            <CardContent className="p-6 flex flex-col gap-4">
                <div className="flex overflow-x-auto no-scrollbar gap-3 pb-1 items-center">
                    <Button
                    variant="outline"
                    className={cn(
                        'flex-shrink-0 bg-white/10 text-white hover:bg-white/20',
                        selectedPreset === 'today' && 'bg-blue-500 text-white hover:bg-blue-600 border-blue-500'
                    )}
                    onClick={() => handlePresetChange('today')}
                    >
                    Hoy
                    </Button>
                    <Button
                    variant="outline"
                    className={cn(
                        'flex-shrink-0 bg-white/10 text-white hover:bg-white/20',
                        selectedPreset === 'month-to-date' && 'bg-blue-500 text-white hover:bg-blue-600 border-blue-500'
                    )}
                    onClick={() => handlePresetChange('month-to-date')}
                    >
                    Días del mes
                    </Button>
                    <Button
                    variant="outline"
                    className={cn(
                        'flex-shrink-0 bg-white/10 text-white hover:bg-white/20',
                        selectedPreset === 'full-month' && 'bg-blue-500 text-white hover:bg-blue-600 border-blue-500'
                    )}
                    onClick={() => handlePresetChange('full-month')}
                    >
                    Mes completo
                    </Button>

                    <Popover>
                    <PopoverTrigger asChild>
                        <Button
                        variant="outline"
                        className={cn(
                            'flex-shrink-0 justify-start bg-white/10 text-left font-normal text-white hover:bg-white/20',
                            selectedPreset === 'custom' && 'bg-blue-500 text-white hover:bg-blue-600 border-blue-500'
                        )}
                        >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {currentFrom && currentTo ? `${format(currentFrom, 'P', { locale: es })} - ${format(currentTo, 'P', { locale: es })}` : ''}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto bg-gray-900 p-0" align="start">
                        <Calendar
                        mode="range"
                        numberOfMonths={2}
                        locale={es}
                        selected={dateRange}
                        defaultMonth={currentFrom}
                        onSelect={handleDateChange}
                        initialFocus
                        />
                    </PopoverContent>
                    </Popover>
                </div>

                <div className="flex overflow-x-auto no-scrollbar gap-2 pb-1 items-center">
                    <Button
                    className="flex-shrink-0 flex items-center gap-2 bg-emerald-500 text-white hover:bg-emerald-500/90"
                    onClick={handleGenerateClick}
                    disabled={loading}
                    >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    Generar
                    </Button>
                    <Button
                    variant="outline"
                    className="flex-shrink-0 flex items-center gap-2 bg-red-500/20 text-red-200 hover:bg-red-500/30"
                    onClick={handleClearCache}
                    >
                    <Trash2 className="h-4 w-4" />
                    Limpiar caché
                    </Button>
                    <div className="flex-shrink-0"><JsonDebugModal data={report} /></div>
                    <div className="flex-shrink-0 flex items-center gap-2 rounded-md bg-white/5 px-3 py-2">
                        <Switch
                            id="force-shopify-products"
                            checked={forceShopify}
                            onCheckedChange={setForceShopify}
                            className="data-[state=checked]:bg-blue-500"
                        />
                        <Label htmlFor="force-shopify-products" className="cursor-pointer text-sm text-white">
                            Forzar datos de Shopify {forceShopify && '(habilitado)'}
                        </Label>
                    </div>
                    <div className="flex-shrink-0"><IndicatorsConfigDialog config={config} onSave={setConfig} /></div>
                </div>
            </CardContent>
        </Card>

        {loading ? (
            <Skeleton className="h-[500px] w-full" />
        ) : !channelData ? (
            <div className="text-center py-10">
                <p>No hay datos disponibles para el período o canal seleccionado.</p>
            </div>
        ) : (
            <div className="space-y-8">
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
                    <KPICard title="Unidades" value={channelData.units.toLocaleString()} icon={Package} />
                    <KPICard title="Venta Neta" value={formatCurrency(channelData.netSales)} icon={DollarSign} />
                    <KPICard title="Precio Prom." value={formatCurrency(channelData.avgPrice)} icon={TrendingUp} />
                    <KPICard title="Pedidos" value={channelData.orders.toLocaleString()} icon={ShoppingCart} />
                    <KPICard title="UPT" value={channelData.upt.toFixed(2)} icon={BarChart3} />
                    <KPICard title="Factura Prom." value={formatCurrency(channelData.avgTicket)} icon={Percent} />
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                    {config.showBrands && <DataTableCard title="Ventas por Marca" data={channelData.brands} totalNetSales={channelData.netSales} />}
                    {config.showLines && <DataTableCard title="Ventas por Línea de Producto" data={channelData.lines} totalNetSales={channelData.netSales} />}
                </div>
            </div>
        )}
    </div>
    </div>
  );
}

const KPICard: FC<{ title: string; value: string; icon: React.ElementType }> = ({ title, value, icon: Icon }) => (
    <Card className="bg-white/5 border-white/10 backdrop-blur-xl">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-4 pt-4">
            <CardTitle className="text-xs font-medium text-white/60 leading-tight">{title}</CardTitle>
            <Icon className="h-3.5 w-3.5 text-white/30 flex-shrink-0" />
        </CardHeader>
        <CardContent className="px-4 pb-4">
            <div className="text-xl font-bold text-white tabular-nums">{value}</div>
        </CardContent>
    </Card>
);

interface DataTableCardProps {
    title: string;
    data: Record<string, { units: number; netSales: number; }>;
    totalNetSales: number;
}

const DataTableCard: FC<DataTableCardProps> = ({ title, data, totalNetSales }) => {
    const tableData = useMemo(() => Object.entries(data).map(([name, values]) => ({
        name,
        units: values.units,
        netSales: values.netSales,
        percentage: totalNetSales > 0 ? (values.netSales / totalNetSales) * 100 : 0,
        avgPrice: values.units > 0 ? values.netSales / values.units : 0,
    })).sort((a,b) => b.netSales - a.netSales), [data, totalNetSales]);

    const formatCurrency = (amount: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

    return (
        <Card className="bg-white/5 border-white/10 backdrop-blur-xl overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between py-4 px-5">
                <CardTitle className="text-sm font-semibold text-white">{title}</CardTitle>
                <ChartModal data={tableData} title={title} />
            </CardHeader>
            <CardContent className="p-0">
                <div className="overflow-x-auto h-72">
                    <table className="w-full min-w-[520px] text-sm">
                        <thead>
                            <tr className="border-b border-white/10">
                                <th className="px-4 py-2.5 text-left text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">{title.includes("Marca") ? "Marca" : "Línea"}</th>
                                <th className="px-4 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">Uds</th>
                                <th className="px-4 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">Venta Neta</th>
                                <th className="px-4 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">%</th>
                                <th className="px-4 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">P. Prom.</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {tableData.map(item => (
                                <tr key={item.name} className="hover:bg-white/5 transition-colors">
                                    <td className="px-4 py-2.5 font-medium text-white whitespace-nowrap">{item.name}</td>
                                    <td className="px-4 py-2.5 text-right text-white/70 whitespace-nowrap">{item.units.toLocaleString()}</td>
                                    <td className="px-4 py-2.5 text-right text-emerald-400 font-medium whitespace-nowrap">{formatCurrency(item.netSales)}</td>
                                    <td className="px-4 py-2.5 text-right text-white/70 whitespace-nowrap">{item.percentage.toFixed(1)}%</td>
                                    <td className="px-4 py-2.5 text-right text-white/70 whitespace-nowrap">{formatCurrency(item.avgPrice)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </CardContent>
        </Card>
    );
};

const chartConfig: ChartConfig = {
    sales: { label: "Ventas", color: "hsl(var(--chart-1))" },
};

const ChartModal: FC<{ data: any[], title: string }> = ({ data, title }) => {
    const chartData = useMemo(() => data.slice(0, 10).map(item => ({ name: item.name, sales: item.netSales })), [data]);
    const formatCurrency = (amount: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5 bg-white/10 text-white hover:bg-white/20 border-white/10 text-xs h-7 px-2">
                    <BarChartHorizontal className="h-3.5 w-3.5" />
                    Gráfico
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl h-[80vh] bg-gray-900/80 border-white/20 text-white backdrop-blur-xl flex flex-col">
                <DialogHeader>
                    <DialogTitle>Gráfico de {title}</DialogTitle>
                    <DialogDescription>
                        Visualización de las 10 principales categorías por ventas netas.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex-grow p-4">
                    <ChartContainer config={chartConfig} className="h-full w-full">
                        <ResponsiveContainer>
                            <BarChart layout="vertical" data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                <XAxis type="number" tickFormatter={(value) => `$${Number(value) / 1000}k`} />
                                <YAxis dataKey="name" type="category" tick={{fontSize: 12}} />
                                <ChartTooltip
                                    cursor={{fill: 'hsla(var(--muted), 0.5)'}}
                                    content={<ChartTooltipContent
                                        className="bg-background/90 backdrop-blur-sm border-border"
                                        formatter={(value) => formatCurrency(value as number)}
                                    />}
                                />
                                <Bar dataKey="sales" fill="var(--color-sales)" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartContainer>
                </div>
            </DialogContent>
        </Dialog>
    );
};


const IndicatorsConfigDialog: FC<{ config: IndicatorsConfig, onSave: (config: IndicatorsConfig) => void }> = ({ config, onSave }) => {
    const [currentConfig, setCurrentConfig] = useState(config);

    const handleSave = () => {
        onSave(currentConfig);
    };

    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button variant="outline" className="bg-blue-500/20 border-blue-500/50 text-blue-200 hover:bg-blue-500/30">
                    <Settings className="mr-2 h-4 w-4" />
                    Configurar Indicadores
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md bg-gray-900/80 border-white/20 text-white backdrop-blur-xl">
                <DialogHeader>
                    <DialogTitle>Configuración de Indicadores</DialogTitle>
                    <DialogDescription>Selecciona el canal de venta y los módulos que deseas visualizar.</DialogDescription>
                </DialogHeader>
                <div className="space-y-6 py-4">
                    <div className="space-y-2">
                        <Label>Canal de Venta</Label>
                        <Select value={currentConfig.channel} onValueChange={(value: Channel) => setCurrentConfig(c => ({...c, channel: value}))}>
                            <SelectTrigger className="bg-gray-800 border-white/20">
                                <SelectValue placeholder="Seleccionar canal..." />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-800 border-white/20">
                                <SelectItem value="all">Todos</SelectItem>
                                <SelectItem value="web">Online</SelectItem>
                                <SelectItem value="pos">POS</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-4">
                        <Label>Módulos a Mostrar</Label>
                        <div className="flex items-center space-x-2">
                            <Checkbox id="showBrands" checked={currentConfig.showBrands} onCheckedChange={(checked) => setCurrentConfig(c => ({...c, showBrands: !!checked}))} />
                            <Label htmlFor="showBrands">Ventas por Marca</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Checkbox id="showLines" checked={currentConfig.showLines} onCheckedChange={(checked) => setCurrentConfig(c => ({...c, showLines: !!checked}))} />
                            <Label htmlFor="showLines">Ventas por Línea de Producto</Label>
                        </div>
                    </div>
                </div>
                <DialogClose asChild>
                    <Button onClick={handleSave} className="w-full mt-4">
                        <Save className="mr-2 h-4 w-4" />
                        Guardar Cambios
                    </Button>
                </DialogClose>
            </DialogContent>
        </Dialog>
    );
};

const JsonDebugModal: FC<{ data: any }> = ({ data }) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <FileJson className="mr-2 h-4 w-4" />
          Ver Datos
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl h-[80vh] bg-gray-900/80 border-white/20 text-white backdrop-blur-xl">
        <DialogHeader>
          <DialogTitle>Datos Crudos del Reporte</DialogTitle>
          <DialogDescription>
            Este es el objeto JSON completo que se está utilizando para renderizar los indicadores.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-auto rounded-lg bg-black/50 p-4">
          <pre className="text-xs text-green-300">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IndicatorsDashboard;
